import { areMonthYearEqual, formatMonthYearPeriod, MonthYearPeriod } from '@common/neo.tax';
import { Button, colors, ModalActions, ModalHeader, Stack, Typography } from '@ui-components/neo.tax';
import { SyntheticEvent } from 'react';

import { CsvLogo, CsvUploadInstructions, useCsvFileUrl } from '@neo.tax/shared';

import { usePayrollMonthExpenseInstructionModalAnalyticIds } from './hooks';
import { EXAMPLE_DATA_STRINGIFIED, HEADERS_STRINGIFIED } from './payroll-month-expense-instruction-modal.constants';
import { PeriodCallout, StyledModal } from './payroll-month-expense-instruction-modal.styled';

type PayrollMonthExpenseInstructionModalProps = {
  isOpen: boolean;
  close: () => void;
  loading: boolean;
  period: MonthYearPeriod;
  submit: () => void;
};

export const PayrollMonthExpenseInstructionModal = ({
  close,
  isOpen,
  loading,
  period,
  submit,
}: PayrollMonthExpenseInstructionModalProps): React.ReactElement => {
  const { payrollMonthExpenseCancelBtnId, payrollMonthExpenseUploadBtnId } =
    usePayrollMonthExpenseInstructionModalAnalyticIds();
  const templateCsvUrl = useCsvFileUrl(HEADERS_STRINGIFIED);
  const exampleCsvUrl = useCsvFileUrl(EXAMPLE_DATA_STRINGIFIED);
  const { end, start } = formatMonthYearPeriod(period);

  const handleClose = (): void => {
    close();
  };

  const onSubmit = (e: SyntheticEvent): void => {
    e.preventDefault();
    submit();
  };

  return (
    <StyledModal isOpen={isOpen}>
      <Stack as="form" direction="column" onSubmit={onSubmit}>
        <Stack direction="column" gap={4}>
          <ModalHeader icon={<CsvLogo />} iconBgColor={colors.white} title="Upload ASC 350-40 Cost CSV" />
          <PeriodCallout direction="row" gap={1} alignItems="center" justifyContent="center" paddingX={4} paddingY={3}>
            <Typography color={colors.gray[600]} fontSize="sm">
              Uploading costs for:
            </Typography>
            <Typography color={colors.gray[900]} fontWeight="medium" fontSize="sm">
              {areMonthYearEqual(period.startMonthYear, period.endMonthYear) ? start : `${start} - ${end}`}
            </Typography>
          </PeriodCallout>
          <Stack direction="column" gap={2}>
            <CsvUploadInstructions
              title="Add all costs for the selected time range to the template."
              templateCsvUrl={templateCsvUrl}
              templateCsvName="asc-employee-cost-csv-upload-template.csv"
              exampleCsvUrl={exampleCsvUrl}
              exampleCsvName="asc-employee-cost-csv-upload-example.csv"
            />
          </Stack>
        </Stack>

        <ModalActions hasTopBorder={false}>
          <Button
            variant="white"
            rounded="regular"
            size="base"
            id={payrollMonthExpenseCancelBtnId}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            id={payrollMonthExpenseUploadBtnId}
          >
            {loading ? 'Loading...' : 'Upload'}
          </Button>
        </ModalActions>
      </Stack>
    </StyledModal>
  );
};
